<script setup>
import { ref } from 'vue';
import Footer from './Footer.vue';
import Header from './Header.vue';
import SEO from '@/components/SEO.vue';

const benefits = ref([
  {
    title: 'High Commission Rates',
    description:
      'Earn up to 30% commission on every sale you generate through your unique affiliate link.',
    icon: '💰',
    color: '#E8F0FF',
  },
  {
    title: 'Easy-to-Use Dashboard',
    description:
      'Track your performance, commissions, and payouts with our intuitive affiliate dashboard.',
    icon: '📊',
    color: '#EBE8FF',
  },
  {
    title: 'Marketing Resources',
    description:
      'Access to promotional materials, banners, and product information to boost your marketing efforts.',
    icon: '🚀',
    color: '#E7F2EA',
  },
]);

const steps = ref([
  {
    number: '1',
    title: 'Sign Up',
    description: 'Complete our simple registration form to join our affiliate program.',
  },
  {
    number: '2',
    title: 'Get Your Link',
    description: 'Receive your unique affiliate link to share with your audience.',
  },
  {
    number: '3',
    title: 'Promote',
    description: 'Share your link through your website, social media, or email list.',
  },
  {
    number: '4',
    title: 'Earn',
    description: 'Get paid commissions for every sale generated through your link.',
  },
]);

const faqs = ref([
  {
    question: 'How much can I earn?',
    answer:
      "You can earn up to 30% commission on each sale made through your affiliate link. There's no cap on how much you can earn.",
  },
  {
    question: 'When do I get paid?',
    answer:
      "Payments are processed monthly for all commissions earned in the previous month, provided you've reached the minimum payout threshold.",
  },
  {
    question: 'Who can join the affiliate program?',
    answer:
      'Our affiliate program is open to bloggers, content creators, influencers, and anyone with an online presence who can promote our products.',
  },
]);

function joinNowAction() {
  window.location.href = 'mailto:hi@seede.ai';
}
</script>

<template>
  <div class="min-h-screen bg-white pt-[60px]">
    <!-- SEO Section -->
    <SEO
      title="Affiliate Program - Earn Commissions"
      description="Join our affiliate program and earn commissions by promoting our products to your audience."
      keywords="affiliate program, commissions, marketing, promotion"
    />

    <!-- Header Section -->
    <Header />

    <!-- Hero Section -->
    <section class="flex flex-col items-center relative overflow-hidden w-full">
      <!-- Animated gradient background -->
      <div class="absolute inset-0 pointer-events-none">
        <img src="/images/home/pattern.svg" class="absolute inset-0" />
      </div>
      <div class="flex flex-col items-center gap-8 pt-16 pb-8 sm:gap-6 relative max-w-[100vw]">
        <div class="flex flex-col gap-3 items-center">
          <h1
            class="text-4xl md:text-5xl font-extrabold text-[#111111] text-center max-w-[800px] leading-tight"
          >
            Affiliate Program
          </h1>
          <p class="text-xl text-black/70 max-w-3xl mx-auto text-center mt-4">
            Join our affiliate program and earn commissions by promoting our products to your
            audience.
          </p>
        </div>
        <button
          class="bg-gradient-to-l from-[#20f6da] to-[#0afc8e] rounded-lg py-3 px-8 hover:shadow-lg transition-all duration-300 transform hover:scale-105 mt-6"
          @click="joinNowAction"
        >
          <span class="text-[#111111] text-base font-medium">Join Now</span>
        </button>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="container flex flex-col items-center gap-8 py-12 sm:py-16">
      <h2 class="text-3xl font-bold text-center text-[#111111] mb-8">
        Why Join Our Affiliate Program?
      </h2>
      <div class="grid md:grid-cols-3 gap-10 w-full max-w-7xl">
        <div
          v-for="(benefit, index) in benefits"
          :key="index"
          class="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300"
        >
          <div
            :style="`background-color: ${benefit.color}`"
            class="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto"
          >
            <span class="text-3xl">{{ benefit.icon }}</span>
          </div>
          <h3 class="text-xl font-semibold text-center text-black/80 mb-4">
            {{ benefit.title }}
          </h3>
          <p class="text-black/60 text-center">
            {{ benefit.description }}
          </p>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto bg-[#EDF6FF] rounded-2xl my-16">
      <h2 class="text-3xl font-bold text-center text-[#111111] mb-12">How It Works</h2>
      <div class="grid md:grid-cols-4 gap-8">
        <div v-for="(step, index) in steps" :key="index" class="text-center">
          <div
            class="w-12 h-12 bg-[#0083fa] rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4"
          >
            {{ step.number }}
          </div>
          <h3 class="text-lg font-semibold text-black/80 mb-2">
            {{ step.title }}
          </h3>
          <p class="text-black/60">{{ step.description }}</p>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <h2 class="text-3xl font-bold text-center text-[#111111] mb-12">
        Frequently Asked Questions
      </h2>
      <div class="space-y-6 max-w-4xl mx-auto">
        <div v-for="(faq, index) in faqs" :key="index" class="bg-white p-6 rounded-lg shadow-md">
          <h3 class="text-xl font-semibold text-black/80 mb-2">
            {{ faq.question }}
          </h3>
          <p class="text-black/60">
            {{ faq.answer }}
          </p>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="px-4 py-12 md:py-24 bg-[#EDF6FF] w-full">
      <div class="w-full max-w-[1094px] relative mx-auto">
        <img
          src="/images/home/seede-cta-bg.svg"
          class="hidden md:block absolute inset-0 w-full h-auto"
        />
        <div
          class="flex flex-col items-center justify-center min-h-[300px] md:h-[465px] relative z-10 px-4"
        >
          <h2 class="text-[#111111] text-2xl md:text-[40px] font-semibold text-center mb-4 md:mb-8">
            Ready to Start Earning?
          </h2>
          <p
            class="text-[#111111] text-sm md:text-base font-normal text-center max-w-[781px] mb-6 md:mb-12"
          >
            Join thousands of successful affiliates who are already earning with us.
          </p>
          <button
            class="bg-gradient-to-l from-[#20f6da] to-[#0afc8e] rounded-lg py-3 px-8 hover:shadow-lg transition-all duration-300"
            @click="joinNowAction"
          >
            <span class="text-[#111111] text-base font-medium">Apply Now</span>
          </button>
        </div>
      </div>
    </section>

    <Footer class="mt-0" />
  </div>
</template>
